<script setup lang="ts">
import IconMaterialSymbolsLightAttachMoney from '~icons/material-symbols-light/attach-money';
import IconMdiTrendingUp from '~icons/mdi/trending-up';

interface Props {
	title: {
		sectionTitle: string[];
		vgv: string[];
		highValueSales: string[];
	};
	description: {
		vgv: string;
		vgvNote: string;
		highValueSales: string;
	};
	areas: {
		itaimBibi: string;
		jardins: string;
		pinheiros: string;
	};
}

defineProps<Props>();
</script>

<template>
	<section class="bg-primary">
		<Container class="divide-y py-11 md:py-20">
			<div class="pb-11 md:pb-20">
				<div class="max-w-[790px]">
					<h3
						class="font-matter-sq text-[28px] leading-[42px] tracking-[-0.88px] text-white md:text-[44px] md:leading-[150%]"
					>
						<span class="relative z-10">{{
							title.sectionTitle[0] + '&nbsp;'
						}}</span>
						<SkewRectangleText>
							<template #default>
								<span class="relative z-10">
									<span class="text-beige">{{ title.sectionTitle[1] }}</span>
									<span class="text-white">{{ title.sectionTitle[2] }}</span>
								</span>
							</template>
						</SkewRectangleText>
					</h3>
				</div>
			</div>
			<div class="flex flex-col divide-y divide-primary-light-3 lg:divide-none">
				<div
					class="flex w-full flex-col-reverse justify-center pb-9 lg:flex-row lg:pb-0"
				>
					<div
						class="relative flex w-full items-center justify-center sm:inset-0 lg:w-1/2"
					>
						<NuxtImg
							loading="lazy"
							:src="'/images/about/first_results_image.webp'"
							alt="Pilar"
							quality="85"
							format="webp"
							width="643"
							height="406"
							class="object-contain pt-1 lg:h-[406px] lg:w-[643px] lg:pt-0"
						/>
					</div>
					<div
						class="flex w-full items-center justify-center md:flex-row lg:w-1/2"
					>
						<div class="flex w-full flex-col gap-4 lg:flex-row">
							<div>
								<IconMdiTrendingUp class="size-9 text-beige lg:size-11" />
							</div>
							<div class="flex flex-col">
								<div class="flex flex-col gap-2 pb-6">
									<h4
										class="font-matter-sq text-xl font-normal leading-[150%] tracking-[-0.64px] text-white md:text-[2rem]"
									>
										{{ title.vgv[0] }}
										<span class="text-beige">{{ title.vgv[1] }}</span>
										{{ title.vgv[2] }}
									</h4>
									<p
										class="self-stretch text-base font-normal leading-[150%] text-white md:text-xl"
									>
										{{ description.vgv }}
									</p>
								</div>
								<div
									class="h-[2px] w-20 border-b-0 border-t-2 border-primary-light-2"
								/>
								<div class="pt-6">
									<p class="text-sm leading-[150%] text-white-dark-4">
										{{ description.vgvNote }}
									</p>
								</div>
							</div>
						</div>
					</div>
				</div>
				<div class="flex flex-col items-center gap-x-11 pb-0 lg:flex-row">
					<div
						class="flex w-full flex-col gap-4 pt-[32px] lg:w-1/2 lg:flex-row lg:pt-[60px]"
					>
						<div>
							<IconMaterialSymbolsLightAttachMoney
								class="size-9 text-beige lg:size-11"
							/>
						</div>
						<div class="flex flex-col gap-x-6 gap-y-3">
							<div class="flex flex-col gap-2">
								<h5
									class="font-matter-sq text-xl font-normal leading-[150%] tracking-[-0.64px] text-white lg:text-[2rem]"
								>
									{{ title.highValueSales[0] }}
									<span class="text-beige">{{ title.highValueSales[1] }}</span>
								</h5>
								<p
									class="text-base font-normal leading-[150%] text-white lg:text-xl"
								>
									{{ description.highValueSales }}
								</p>
							</div>
							<div
								class="h-[2px] w-20 border-b-0 border-t-2 border-primary-light-2"
							/>
							<div
								class="grid grid-cols-2 gap-5 text-base text-white lg:grid-cols-3 lg:text-lg"
							>
								<div>
									<span>
										<span class="text-beige">+26%</span>
										{{ areas.itaimBibi }}
									</span>
								</div>
								<div>
									<span>
										<span class="text-beige">+25%</span>
										{{ areas.jardins }}
									</span>
								</div>
								<div>
									<span class="text-beige">+40%</span>
									{{ areas.pinheiros }}
								</div>
							</div>
						</div>
					</div>
					<div
						class="relative z-10 flex w-full items-center justify-center md:h-[500px] lg:w-1/2"
					>
						<span
							class="absolute z-20 hidden h-px shrink-0 skew-y-[-45deg] self-stretch bg-primary-light-4 lg:-left-20 lg:top-12 lg:block lg:w-[100px] xl:-left-28 xl:top-10 2xl:-left-32 2xl:top-10 2xl:w-[165px]"
							aria-hidden="true"
						/>
						<NuxtImg
							loading="lazy"
							:src="'/images/about/second_results_image.webp'"
							alt="Pilar"
							format="webp"
							width="653"
							height="415"
							class="inset-0 z-20 object-contain md:absolute lg:h-[415px] lg:w-[653px]"
						/>
					</div>
				</div>
			</div>
		</Container>
	</section>
</template>
